<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import Image from '@uiKit/elements/Image.vue'
import Text from '@uiKit/elements/Text.vue'

// A square thumbnail that falls back to the first letter of its title: shows
// `src` when there is one, and the initial when it's missing or fails to load —
// so a missing logo / cover never renders as a broken image or a blank box.
interface Props {
  /** Title the initial is taken from (bank name, property name, …). */
  name: string
  src?: string | null
  /** Edge length in px. */
  size?: number
  /** Corner radius (CSS length). */
  radius?: string
  /** How many leading letters stand in for the image ("EM" for Emirates NBD). */
  letters?: 1 | 2
  /** `contain` keeps a wordmark whole inside the tile; photos fill it (`cover`). */
  fit?: 'cover' | 'contain'
}

const props = withDefaults(defineProps<Props>(), {
  src: null,
  size: 40,
  radius: '8px',
  letters: 1,
  fit: 'cover',
})

const FALLBACK_INITIAL = '?'
// The letter scales with the tile so it reads the same at 36px and 84px; a
// two-letter monogram runs smaller so it still sits inside the tile.
const INITIAL_SCALE = 0.42
const TWO_LETTER_SCALE = 0.3

const failed = ref(false)
watch(
  () => props.src,
  () => {
    failed.value = false
  },
)

const showImage = computed(() => Boolean(props.src) && !failed.value)
const initial = computed(
  () => props.name.trim().slice(0, props.letters).toUpperCase() || FALLBACK_INITIAL,
)

const boxStyle = computed(() => ({
  width: `${props.size}px`,
  height: `${props.size}px`,
  borderRadius: props.radius,
}))
const initialStyle = computed(() => ({
  fontSize: `${Math.round(props.size * (props.letters === 2 ? TWO_LETTER_SCALE : INITIAL_SCALE))}px`,
  lineHeight: 1,
}))
</script>

<template>
  <Box align="center" justify="center" class="initials-avatar" :style="boxStyle">
    <Image
      v-if="showImage && src"
      :src="src"
      alt=""
      class="initials-avatar__img"
      :class="{ 'initials-avatar__img--contain': fit === 'contain' }"
      :style="{ borderRadius: radius }"
      @error="failed = true"
    />
    <Text v-else is="span" type="body" weight="bold" color="muted" :style="initialStyle">
      {{ initial }}
    </Text>
  </Box>
</template>

<style scoped>
.initials-avatar {
  flex-shrink: 0;
  overflow: hidden;
  background: var(--p-surface-100);
  border: 1px solid var(--p-sliceal-gray-100);
  box-sizing: border-box;
}

.initials-avatar__img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.initials-avatar__img--contain {
  object-fit: contain;
  padding: 15%;
  box-sizing: border-box;
}
</style>
